
import { Ref, ref } from "vue";
import { FilterOptions } from "@/filters/options/FilterOptions";
import { DefaultStaticContentHandler } from "./handlers/DefaultStaticContentHandler";
import { NetherlandsStaticContentHandler } from "./handlers/NetherlandsStaticContentHandler";
import { SwedenStaticContentHandler } from "./handlers/SwedensStaticContentHandler";
import { MedicineStaticContentHandler } from "./handlers/MedicineStaticContentHandler";
import { BelgiumStaticContentHandler } from "./handlers/BelgiumStaticContentHandler";
import { FranceStaticContentHandler } from "./handlers/FranceStaticContentHandler";
import { NorwayStaticContentHandler } from "./handlers/NorwayStaticContentHandler";
import { AerospaceEngineeringStaticContentHandler } from "./handlers/AerospaceEngineeringContentHandler";
import { ItalyStaticContentHandler } from "./handlers/ItalyStaticContentHandler";
import { GermanyStaticContentHandler } from "./handlers/GermanyStaticContentHandler";
import { EngineeringTechnologyStaticContentHandler } from "./handlers/EngineeringTechnologyContentHandler";
import { IHandler } from "./IHandler";
import { FilterSelectionState } from "@/filters/selection/FilterSelectionState";
import { IStaticContentOrNull } from "./IStaticContent";
import { Degree } from "@/models/Degree";
import { injectable } from "inversify";
import { ConvergingTask } from "@/platform/ConvergingTask";
import { IStaticContentInput } from "./IStaticContentInput";
import { FilterSelection } from "@/filters/selection/FilterSelection";
import { ISubscriber } from "@studyportals/event-aggregation-service-interface";
import { EmbeddedStatePropagatedEvent } from "@/url/events/EmbeddedStatePropagatedEvent";
import { EventAggregationService } from '@/platform/EventAggregationService';
import { IFaq } from "./IFaq";
import { SortingState } from "@/sorting/SortingState";
import { SortingSelection } from "@/sorting/SortingSelection";

@injectable()
export class StaticContentBuilder
	extends ConvergingTask<IStaticContentOrNull, IStaticContentInput>
	implements ISubscriber<EmbeddedStatePropagatedEvent> {

	private handler: IHandler;
	public staticContent: Ref<IStaticContentOrNull> = ref(null);

	public readonly faqs: Ref<IFaq[]> = ref([]);

	public constructor(
		eventAggregationService: EventAggregationService,
		filterSelection: FilterSelection,
		filterOptions: FilterOptions,
		sortingSelection: SortingSelection,
	) {
		super();

		this.handler = new NetherlandsStaticContentHandler(filterOptions, "1");
		this.handler
			.setNext(new SwedenStaticContentHandler(filterOptions, "26"))
			.setNext(new MedicineStaticContentHandler(filterOptions, "10"))
			.setNext(new BelgiumStaticContentHandler(filterOptions, "4"))
			.setNext(new FranceStaticContentHandler(filterOptions, "10"))
			.setNext(new NorwayStaticContentHandler(filterOptions, "19"))
			.setNext(new AerospaceEngineeringStaticContentHandler(filterOptions, "37"))
			.setNext(new ItalyStaticContentHandler(filterOptions, "15"))
			.setNext(new GermanyStaticContentHandler(filterOptions, "11"))
			.setNext(new EngineeringTechnologyStaticContentHandler(filterOptions, "7"))
			.setNext(new DefaultStaticContentHandler(filterOptions, null))

		void this.refresh({
			filterState: filterSelection.state,
			sortState: sortingSelection.state,
			degree: Degree.forPortal()
		});

		eventAggregationService.subscribeTo(EmbeddedStatePropagatedEvent.EventType, this);
	}

	public notify(event: EmbeddedStatePropagatedEvent): void {
		if (EmbeddedStatePropagatedEvent.is(event)) {
			void this.refresh({
				filterState: event.state.filterSelectionState,
				sortState: event.state.sortingState,
				degree: event.state.degreeOrNull ?? Degree.forPortal()
			});
		}
	}

	private async build(degree: Degree, filterState: FilterSelectionState, sortState: SortingState): Promise<IStaticContentOrNull> {
		if (degree.value !== Degree.master.value) {
			return Promise.resolve(null);
		}

		return this.handler.handle(filterState, sortState);
	}

	private async refresh(input: IStaticContentInput): Promise<void> {
		await this.execute(input);
	}

	protected override async run(input: IStaticContentInput): Promise<IStaticContentOrNull> {
		return await this.build(input.degree, input.filterState, input.sortState);
	}

	protected override commit(result: IStaticContentOrNull): void {
		this.staticContent.value = result;
	}
}
